<template>
  <FormulateForm
    v-model="formValues"
    :errors="formErrors"
    ref="formRef"
    class="detail-info-section"
  >
    <div class="row detail-info-section__title">
      <h4 class="col-md-8 col-12 card-title">Detail Template</h4>
      <div class="col-12 col-md-4 d-flex justify-content-end gap-2">
        <McButton
          variant="secondary"
          icon="icon-outline_close"
          :text="$t('global.action.cancel')"
          :disabled="!detailInfoChanged"
          @click="isOpenCancelModal = true"
        />
        <McButton
          type="submit"
          icon="icon-outline_save"
          :text="this.$t('global.save')"
          :disabled="!detailInfoChanged"
          @click="handleSubmit"
        />
        <McConfirmation
          v-model="isOpenCancelModal"
          centered
          title="Batal"
          okTitle="Ya"
          cancelTitle="Tidak"
          @handle-ok="handleCancel"
          @cancel="isOpenCancelModal = false"
        >
          <p>
            {{ $t('global.text.modal.cancelChange') }}
            <strong>{{ $t('delivery.infoDelivery') }}</strong
            >?
          </p>
        </McConfirmation>
      </div>
    </div>

    <b-card>
      <h4 class="card-title detail-info-section__sub-title">Informasi Template</h4>

      <div class="row">
        <div v-for="field in basicInfoFields" :class="field.col">
          <FormulateInput
            v-bind="field"
            class="p-0"
            validation-behavior="live"
            :help="field.validation.includes('required')"
          />
        </div>
      </div>

      <hr />

      <h4 class="col-12 card-title detail-info-section__sub-title">Informasi Pengiriman</h4>

      <div class="row">
        <div v-for="field in detailFields" class="col-12 col-md-3">
          <MktUserSelect
            v-if="field.name === 'marketing_user_id'"
            v-model="formValues[field.name]"
            :field="field"
            :fullObject.sync="fullObjects[field.name]"
            :customerObj="fullObjects.shipped_customer_id"
            :isShowingData="isShowingData"
          />
          <BilledCustValueHelp
            v-else-if="field.name === 'billed_customer_id'"
            v-model="formValues[field.name]"
            :field="field"
            :fullObject.sync="fullObjects[field.name]"
            :formValues="formValues"
            :shippedCustFullObj="fullObjects.shipped_customer_id"
            :isShowingData="isShowingData"
          />
          <FormulateInput
            v-else
            v-bind="field"
            class="p-0"
            validation-behavior="live"
            :fullObject.sync="fullObjects[field.name]"
          />
        </div>
      </div>

      <div class="d-flex gap-2 detail-info-section__other-info-title">
        <p class="m-0">Informasi Lainnya</p>
        <McToggle v-model="isShowOtherInfo" />
      </div>

      <div v-show="isShowOtherInfo" class="row">
        <div class="col-12 col-sm-6 d-flex flex-column gap-2">
          <div v-for="field in otherInfoFields">
            <DeliveryNoteSelect
              v-if="field.name === 'customer_notes'"
              v-model="formValues[field.name]"
              :field="field"
              :fullObject.sync="fullObjects[field.name]"
              :customerId="formValues.shipped_customer_id"
            />
            <FormulateInput v-else v-bind="field" validation-behavior="live" class="p-0" />
          </div>
        </div>
      </div>

      <hr />

      <div class="row">
        <div v-for="addressItem in addressItems" class="col-12 col-md-6">
          <h4 class="card-title detail-info-section__sub-title">Lokasi {{ addressItem.label }}</h4>

          <AddressReadOnly
            class="detail-info-section__address-read-only"
            :type="addressItem.type"
            :fullObject="fullObjects[addressItem.fullObjectKey]"
          />

          <div class="row">
            <template v-for="field in addressItem.fields">
              <div :class="field.classCol">
                <div
                  v-if="field.type === 'value-help-v2'"
                  class="detail-info-section__address-input"
                >
                  <AddressValueHelp
                    :field="field"
                    :otherAdressFullObject="fullObjects[addressItem.otherObj]"
                    :formValues="formValues"
                    :isShowingData="isShowingData"
                    :fullObject.sync="fullObjects[field.name]"
                    :value="formValues[field.name]"
                    @input="handleChangeFormValues({ key: field.name, value: $event })"
                  />
                  <ButtonCopy
                    buttonText="Salin Alamat"
                    icon="icon-outline_copy"
                    :toastTitle="field.label"
                    :valueToCopy="fullObjects[field.name]?.name"
                    :disabled="!fullObjects[field.name]?.name"
                  />
                </div>
                <FormulateInput
                  v-else-if="field.type === 'time-duration'"
                  v-bind="field"
                  class="p-0"
                  validation-behavior="live"
                >
                  <template #prefix>
                    <div class="formulate-input-prefix">
                      <span>+</span>
                    </div>
                  </template>
                </FormulateInput>
                <FormulateInput v-else v-bind="field" class="p-0" validation-behavior="live" />
              </div>
            </template>
          </div>
        </div>
      </div>

      <template v-if="isCustomFieldShown">
        <hr />

        <h4 class="card-title detail-info-section__sub-title">Kolom Tambahan</h4>

        <div v-if="customFields.length > 0" class="row">
          <div v-for="customField in customFields" class="col-12 col-md-3" :key="customField.udfId">
            <FormulateInput v-bind="customField" validation-behavior="live" class="p-0" />
          </div>
        </div>
      </template>

      <hr />

      <h4 class="card-title detail-info-section__sub-title">Kendaraan</h4>

      <BoxTypeSelect
        v-model="formValues.box_type_id"
        :field="fleetField"
        :fullObject.sync="fullObjects.box_type_id"
        :totalWeights="totalWeights"
      />

      <template v-if="isSubscribeVMS">
        <hr />

        <h4 class="card-title detail-info-section__sub-title">Vendor</h4>

        <div class="row">
          <div class="col-12 col-md-3">
            <FormulateInput
              v-bind="vendorField"
              class="p-0"
              validation-behavior="live"
              :fullObject.sync="fullObjects.vendor_id"
            />
          </div>
        </div>
      </template>
    </b-card>
  </FormulateForm>
</template>

<script>
import { mapGetters } from 'vuex'

import McButton from '@/components/McButton'
import McToggle from '@/components/McToggle'
import McConfirmation from '@/components/McConfirmation'

import MktUserSelect from '../wrapper/MktUserSelect'
import BilledCustValueHelp from '../wrapper/BilledCustValueHelp'
import AddressValueHelp from '../wrapper/AddressValueHelp'
import DeliveryNoteSelect from '../wrapper/DeliveryNoteSelect'
import BoxTypeSelect from '../wrapper/BoxTypeSelect'

import AddressReadOnly from '../components/AddressReadOnly'
import ButtonCopy from '../components/ButtonCopy'

import * as CustomField from '@/services/rest-api/lib/setting/custom-field'

import basicInfoFields from '../lib/fields/basic-info-fields.js'
import detailFields from '../lib/fields/detail-fields.js'
import otherInfoFields from '../lib/fields/other-info-fields.js'
import { originFields, destinationFields } from '../lib/fields/address-fields.js'
import fleetField from '../lib/fields/fleet-field.js'
import vendorField from '../lib/fields/vendor-field.js'
import errorMappings from '../lib/constants/error-mappings.js'

import { EventBus } from '../lib/event-bus.js'

import * as TemplateDeliveryOrder from '@/services/rest-api/lib/delivery/template'

import isEqual from 'lodash/isEqual'
import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'

export default {
  components: {
    McButton,
    McToggle,
    McConfirmation,
    MktUserSelect,
    BilledCustValueHelp,
    AddressValueHelp,
    DeliveryNoteSelect,
    BoxTypeSelect,
    AddressReadOnly,
    ButtonCopy,
  },
  props: {
    totalWeights: {},
  },
  emits: ['update:totalWeights', 'update:totalVolumes', 'update:templateName', 'refetch-data'],
  data() {
    return {
      basicInfoFields,
      detailFields,
      otherInfoFields,
      originFields,
      destinationFields,
      fleetField,
      vendorField,

      showResponse: {},
      formValues: {},
      initialFormValues: {},
      invalidFormValues: {},
      fullObjects: {},
      formErrors: {},

      defaultCustomFields: [],

      isShowOtherInfo: false,
      isSubmitting: false,
      isShowingData: false,
      isOpenCancelModal: false,
    }
  },
  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
    }),
    templateId() {
      return this.$route.params.id
    },
    detailInfoChanged() {
      let currentFormValues = this.formValues
      let oldFormValues = {}

      if (this.templateId) oldFormValues = cloneDeep(this.initialFormValues)

      const res = this.filterUnchangedData(currentFormValues, oldFormValues)
      return res[1]
    },
    addressItems() {
      return [
        {
          type: 'origin',
          label: 'Asal',
          fields: originFields,
          fullObjectKey: 'origin_address_id',
          otherObj: 'destination_address_id',
        },
        {
          type: 'destination',
          label: 'Tujuan',
          fields: destinationFields,
          fullObjectKey: 'destination_address_id',
          otherObj: 'origin_address_id',
        },
      ]
    },
    customFields() {
      let transformed = []

      /**
       * When creating a new DO, use the default custom field setting
       * When updating a DO, use the fields from the response
       */
      transformed = this.transformCustomFieldResponse(this.defaultCustomFields)

      return transformed
    },
    isCustomFieldShown() {
      return this.customFields.length > 0
    },
    isSubscribeVMS() {
      return this.hasAccessAll(['vms:create', 'vms:read', 'vms:update', 'vms:delete'])
    },
  },
  watch: {
    formValues(newValue) {
      if (this.isShowingData) return
      if (Object.keys(this.formErrors).length === 0) return

      for (const [key, _] of Object.entries(this.invalidFormValues)) {
        if (this.invalidFormValues[key] === newValue[key]) continue

        this.$delete(this.formErrors, key)
        this.$delete(this.invalidFormValues, key)
      }
    },
  },
  created() {
    EventBus.$on('show-template-data', this.showData)
  },
  async mounted() {
    await this.getDefaultCustomFields()

    if (this.templateId === undefined) return

    await this.showData()
  },
  beforeDestroy() {
    EventBus.$off('show-template-data', this.showData)
  },
  methods: {
    filterUnchangedData(newValue, oldValue) {
      const keys = new Set([...Object.keys(newValue), ...Object.keys(oldValue)])
      const _newValue = {}

      keys.forEach((key) => {
        // if both value is falsy
        if (!oldValue[key] && !newValue[key]) {
          // Zero and empty string are considered different values
          if (
            (oldValue[key] === 0 && newValue[key] === '') ||
            (oldValue[key] === '' && newValue[key] === 0)
          ) {
            _newValue[key] = newValue[key]
          }
          return
        }

        // if oldValue has same value with newValue
        if (oldValue[key] == newValue[key]) return

        // if the oldValue is 'Invalid date' and newValue is null
        if (oldValue[key] === 'Invalid date' && newValue[key] === null) return

        // if it is array
        if (Array.isArray(oldValue[key]) || Array.isArray(newValue[key])) {
          // if oldValue is undefined but the newValue is empty array
          if (!oldValue[key] && newValue[key].length === 0) return

          // matching oldValue and newValue array
          const sortedNewValue = newValue[key]?.length ? newValue[key].sort() : newValue[key]
          const sortedOldValue = oldValue[key] ? oldValue[key].sort() : oldValue[key]

          const isSameArray = isEqual(sortedNewValue, sortedOldValue)

          if (isSameArray) return
        }

        _newValue[key] = newValue[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },

    handleChangeFormValues({ key, value }) {
      this.$set(this.formValues, key, value)
    },

    async getDefaultCustomFields() {
      const res = await CustomField.get()

      if (res.errors) return

      this.defaultCustomFields = res.data
    },

    parseShowResponse() {
      let fields = [
        ...this.basicInfoFields,
        ...this.detailFields,
        ...this.otherInfoFields,
        ...this.originFields,
        ...this.destinationFields,
        this.fleetField,
      ]

      if (this.isSubscribeVMS) {
        fields = [...fields, this.vendorField]
      }

      fields.forEach((field) => {
        this.$set(this.formValues, field.name, get(this.showResponse, field.showKey))
        this.$set(this.initialFormValues, field.name, get(this.showResponse, field.showKey))

        // set full objects for customer_notes
        if (field.name === 'customer_notes') {
          if (this.showResponse[field.name].length === 0) {
            this.$set(this.fullObjects, field.name, [])
          } else {
            const newFullObjects = this.showResponse[field.name].map((value, id) => {
              return {
                id,
                [field.trackBy]: value,
              }
            })

            this.$set(this.fullObjects, field.name, newFullObjects)
          }
        }

        if (field.name === field.showKey) return

        const keys = field.showKey.split('.')
        const fullObject = Object.assign({}, this.showResponse[keys[0]])

        if (fullObject.id === null || fullObject.id === undefined) return

        if (fullObject.is_error === false) {
          this.$set(this.fullObjects, field.name, fullObject)
          return
        }

        this.$delete(this.formValues, field.name)
        this.$delete(this.initialFormValues, field.name)

        this.$set(this.formErrors, field.name, 'Data telah dihapus dari sistem')
        this.$set(this.invalidFormValues, field.name, this.formValues[field.name])
      })

      if (this.isCustomFieldShown) {
        this.showResponse.user_definition_fields.forEach((field) => {
          this.$set(this.formValues, field.key, field.value)
          this.$set(this.initialFormValues, field.key, field.value)
        })
      }
    },

    async showData() {
      this.isShowingData = true
      const res = await TemplateDeliveryOrder.show(this.templateId)

      if (res.data == undefined && res.errors) {
        this.$mcToast.error('Terdapat kesalahan format pada input anda')
        this.isShowingData = false
        return
      }

      this.showResponse = res.data

      await this.$nextTick()

      this.parseShowResponse()
      await this.$nextTick()

      this.$emit('update:totalWeights', this.showResponse.load_weight)
      this.$emit('update:totalVolumes', this.showResponse.load_volume)
      this.$emit('update:templateName', this.showResponse.name)

      if (this.formValues.customer_notes?.length > 0 || this.formValues.note?.length > 0) {
        this.isShowOtherInfo = true
      }

      if (res.errors) {
        await this.assignErrors(res.errors)
      }

      this.isShowingData = false
    },

    transformCustomFieldResponse(fields = []) {
      const fieldTypeMapping = { TEXT: 'text', NUMBER: 'number', DATE: 'date-picker' }
      const transformed = cloneDeep(fields)
        .map((field) => {
          // Assign null as default value
          if (this.isAdding) {
            field.value = null
          }

          const validation = []

          if (field.is_required) {
            validation.push('required:trim')
            field.help = true
          }

          if (field.type === 'NUMBER') {
            field._type = fieldTypeMapping.NUMBER
            field.type = fieldTypeMapping.TEXT
            validation.push('number')
          } else {
            field.type = fieldTypeMapping[field.type]
          }

          if (validation.length > 0) {
            field.validation = validation.join('|')
          }

          field.label = field.name
          field.name = field.key

          return field
        })
        .filter((field) => field.is_active)

      return transformed
    },

    formatFormValues(data) {
      return Object.assign(
        {},
        ...Object.keys(data).map((key) => {
          const value = data[key]

          if (value === false) {
            return { [key]: null }
          }

          if (key === 'vendor_id' && typeof value === 'undefined') {
            return { [key]: null }
          } else if (this.customFields.some((field) => field.key === key)) {
            // Formatting submitted custom fields data
            const field = this.customFields.find((field) => field.key === key)

            if (field._type === 'number') {
              if (value == '0') return { [key]: Number(value) }
              if (field.is_required) return { [key]: value === '' ? null : Number(value) }

              return { [key]: Number(value) || null }
            }
          }

          return { [key]: value }
        })
      )
    },

    async submitStore() {
      this.isSubmitting = true

      const parsed = this.formatFormValues(this.formValues)

      const res = await TemplateDeliveryOrder.store(parsed)

      if (res.errors) {
        this.isSubmitting = false

        if (res.status !== 400) return

        await this.assignErrors(res.errors)
        return
      }

      this.$mcToast.success('Berhasil disimpan.')
      this.isSubmitting = false

      this.$router.push({
        name: 'Setting Template Delivery Order Detail',
        params: { id: res.data.id },
      })

      this.$emit('refetch-data')
    },

    async submitPatch() {
      this.isSubmitting = true

      const [data, hasChanged] = this.filterUnchangedData(this.formValues, this.initialFormValues)

      if (!hasChanged) {
        this.isSubmitting = false
        return
      }

      const parsed = this.formatFormValues(data)

      const res = await TemplateDeliveryOrder.update(this.templateId, parsed)

      if (res.errors) {
        this.isSubmitting = false
        if (res.status !== 400) return

        await this.assignErrors(res.errors)

        return
      }

      this.$mcToast.success('Berhasil disimpan.')
      this.isSubmitting = false

      await this.showData()
    },

    async handleSubmit() {
      if (this.isSubmitting) return this.$mcToast.warning('Mohon tunggu')

      if (this.$refs.formRef.hasErrors) {
        this.$mcToast.error('Terdapat kesalahan format pada input anda')
        return
      }

      if (this.templateId) {
        await this.submitPatch()
      } else {
        await this.submitStore()
      }
    },

    async assignErrors(errors) {
      if (Object.keys(errors).length === 0) return

      this.$mcToast.error('Terdapat kesalahan format pada input anda')
      for (const [key, value] of Object.entries(errors)) {
        if (key === 'billed_customer_id') continue

        if (value[0] === 'value not found') {
          this.$set(this.fullObjects, key, {
            name: ' ',
          })
        }

        let message = errorMappings[value[0]]
        this.$set(this.formErrors, key, message)
        this.$set(this.invalidFormValues, key, this.formValues[key])
      }

      await this.$nextTick()
    },

    handleCancel() {
      this.formValues = {}
      this.parseShowResponse()

      this.isOpenCancelModal = false
    },
  },
}
</script>

<style lang="scss" scoped>
.read-only {
  .label {
    color: $mep-color-text-200;
  }

  .value {
    color: $mep-color-text-300;

    &.bold {
      color: $mep-color-text-400;
      font-weight: 600;
    }
  }
}

.detail-info-section {
  hr {
    margin-top: 20px;
    margin-bottom: 20px;
  }

  &__title {
    margin-top: 4px;
    margin-bottom: 12px;
  }

  &__sub-title {
    margin-bottom: 12px;
  }

  &__other-info-title {
    margin-top: 20px;
    margin-bottom: 20px;
  }

  &__address-read-only {
    margin-top: 24px;
    margin-bottom: 24px;
  }

  &__address-input {
    margin-bottom: 20px;
  }
}
</style>
